/* [BD feat] Product Info Modals - Dynamic System */

(function() {
  'use strict';

  let modalContainer = null;
  let focusableElements = [];
  let firstFocusableElement = null;
  let lastFocusableElement = null;

  function initModals() {
    const triggers = document.querySelectorAll('[data-modal-type]');
    
    if (!triggers.length) return;

    // Create modal container on first load
    createModalContainer();

    // Attach click handlers
    triggers.forEach(trigger => {
      trigger.addEventListener('click', function() {
        const modalType = this.getAttribute('data-modal-type');
        const modalDataStr = this.getAttribute('data-modal-data');
        
        try {
          const modalData = JSON.parse(modalDataStr);
          openModal(modalType, modalData);
        } catch (error) {
          console.error('[BD] Modal data parse error:', error);
        }
      });
    });

    // Close on escape key
    document.addEventListener('keydown', handleEscapeKey);
  }

  function createModalContainer() {
    if (modalContainer) return;

    modalContainer = document.createElement('div');
    modalContainer.className = 'bd-modal';
    modalContainer.setAttribute('aria-hidden', 'true');
    modalContainer.setAttribute('role', 'dialog');
    modalContainer.setAttribute('aria-modal', 'true');
    modalContainer.setAttribute('aria-labelledby', 'bd-modal-title');
    
    modalContainer.innerHTML = `
      <div class="bd-modal__overlay" data-modal-close aria-label="Close modal"></div>
      <div class="bd-modal__container">
        <button class="bd-modal__close" type="button" data-modal-close aria-label="Close modal">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M12.5 12.5L6.50002 6.50002M6.50002 6.50002L0.5 0.5M6.50002 6.50002L12.5 0.5M6.50002 6.50002L0.5 12.5" stroke="#F9F7F5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="bd-modal__content"></div>
      </div>
    `;

    document.body.appendChild(modalContainer);

    // Attach close handlers
    const closeButtons = modalContainer.querySelectorAll('[data-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', closeModal);
    });
  }

  function openModal(type, data) {
    if (!modalContainer) return;

    const contentContainer = modalContainer.querySelector('.bd-modal__content');
    contentContainer.innerHTML = '';

    // Escape HTML in data to prevent XSS
    const safeData = escapeModalData(data);

    // Generate content based on type
    let html = '';
    switch(type) {
      case 'info':
        html = generateInfoModal(safeData);
        break;
      case 'fabric':
        html = generateFabricModal(safeData);
        break;
      case 'showroom':
        html = generateShowroomModal(safeData);
        break;
      default:
        // Default case: show basic info modal
        html = generateInfoModal(safeData);
        console.warn('[BD Modal] Unknown modal type:', type, '- using default info modal');
        break;
    }

    contentContainer.innerHTML = html;

    // Show modal
    modalContainer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bd-modal-open');

    // Setup focus trap
    setupFocusTrap();

    // Re-attach accordion listeners if showroom
    if (type === 'showroom') {
      attachAccordionListeners();
    }
  }

  function escapeModalData(data) {
    if (typeof data !== 'object' || data === null) return data;
    
    const escaped = {};
    
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key];
        
        if (typeof value === 'string') {
          // Escape HTML but preserve URLs and basic formatting
          if (key.includes('Link') || key.includes('Url') || key.includes('url') || key.includes('link')) {
            // Don't escape URLs
            escaped[key] = value;
          } else if (key === 'additionalContent' || key === 'description') {
            // Allow some HTML in rich text fields but escape dangerous tags
            escaped[key] = sanitizeHtml(value);
          } else {
            // Escape all HTML
            escaped[key] = escapeHtml(value);
          }
        } else if (Array.isArray(value)) {
          // Recursively escape arrays
          escaped[key] = value.map(item => escapeModalData(item));
        } else if (typeof value === 'object') {
          // Recursively escape nested objects
          escaped[key] = escapeModalData(value);
        } else {
          escaped[key] = value;
        }
      }
    }
    
    return escaped;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function sanitizeHtml(html) {
    // Allow basic formatting tags but escape dangerous ones
    const div = document.createElement('div');
    div.innerHTML = html;
    
    // Remove script tags and other dangerous elements
    const scripts = div.querySelectorAll('script, iframe, object, embed');
    scripts.forEach(script => script.remove());
    
    return div.innerHTML;
  }

  function setupFocusTrap() {
    // Get all focusable elements
    focusableElements = Array.from(
      modalContainer.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

    if (focusableElements.length === 0) return;

    firstFocusableElement = focusableElements[0];
    lastFocusableElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstFocusableElement.focus();

    // Add keydown listener for tab trap
    modalContainer.addEventListener('keydown', handleTabKey);
  }

  function handleTabKey(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusableElement) {
        e.preventDefault();
        lastFocusableElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusableElement) {
        e.preventDefault();
        firstFocusableElement.focus();
      }
    }
  }

  function handleEscapeKey(e) {
    if (e.key === 'Escape' && modalContainer && modalContainer.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  }

  function closeModal() {
    if (!modalContainer) return;

    modalContainer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bd-modal-open');

    // Remove tab trap listener
    modalContainer.removeEventListener('keydown', handleTabKey);

    // Clear content after animation
    setTimeout(() => {
      const contentContainer = modalContainer.querySelector('.bd-modal__content');
      if (contentContainer) {
        contentContainer.innerHTML = '';
      }
    }, 300);
  }

  function generateInfoModal(data) {
    let html = '';

    if (data.heading) {
      html += `<h2 class="bd-modal__heading subheading--s" id="bd-modal-title">${data.heading}</h2>`;
    }

    if (data.description) {
      const textAlign = data.alignLeft ? 'text-align: left;' : '';
      html += `<p class="bd-modal__description" style="white-space: pre-wrap; ${textAlign}">${data.description}</p>`;
    }

    if (data.buttonLabel) {
      html += `
        <a href="${data.buttonLink || '#'}" class="bd-modal__button">
          <span class="bd-modal__button-label button-label-medium">${data.buttonLabel}</span>
          ${data.buttonIcon ? `
            <span class="bd-modal__button-icon">
              <img src="${data.buttonIcon}" alt="" width="38" height="38" loading="lazy">
            </span>
          ` : ''}
        </a>
      `;
    }

    if (data.image1 || data.image2) {
      html += '<div class="bd-modal__images">';
      if (data.image1) {
        html += `<img src="${data.image1}" alt="${data.image1Alt || ''}" class="bd-modal__image" loading="lazy">`;
      }
      if (data.image2) {
        html += `<img src="${data.image2}" alt="${data.image2Alt || ''}" class="bd-modal__image" loading="lazy">`;
      }
      html += '</div>';
    }

    if (data.additionalContent) {
      html += `<div class="bd-modal__richtext text--xs">${data.additionalContent}</div>`;
    }

    return html;
  }

  function generateFabricModal(data) {
    let html = '';

    if (data.heading) {
      html += `<h2 class="bd-modal__heading subheading--s" id="bd-modal-title">${data.heading}</h2>`;
    }

    if (data.description) {
      html += `<p class="bd-modal__description" style="white-space: pre-wrap;">${data.description}</p>`;
    }

    if (data.buttonLabel) {
      html += `
        <a href="${data.buttonLink || '#'}" class="bd-modal__button">
          <span class="bd-modal__button-label button-label-medium">${data.buttonLabel}</span>
          ${data.buttonIcon ? `
            <span class="bd-modal__button-icon">
              <img src="${data.buttonIcon}" alt="" width="38" height="38" loading="lazy">
            </span>
          ` : ''}
        </a>
      `;
    }

    if (data.fabrics && data.fabrics.length > 0) {
      html += '<div class="bd-fabric-table"><div class="bd-fabric-table__scroll">';

      data.fabrics.forEach(fabric => {
        if (!fabric.name) return;

        html += '<div class="bd-fabric-table__column">';

        if (fabric.image) {
          html += `<img src="${fabric.image}" alt="${fabric.name}" class="bd-fabric-table__image" loading="lazy" width="132" height="132">`;
        } else {
          // If no image (e.g. for the Labels column), output an empty div to maintain spacing
          html += `<div class="bd-fabric-table__image" style="width: 132px; height: 132px;"></div>`;
        }

        html += `<div class="bd-fabric-table__row"><span class="bd-fabric-table__text">${fabric.name}</span></div>`;

        if (fabric.details && fabric.details.length > 0) {
          fabric.details.forEach(detail => {
            html += `
              <div class="bd-fabric-table__row">
                <span class="bd-fabric-table__text">${detail}</span>
              </div>
            `;
          });
        }

        html += '</div>';
      });

      html += '</div></div>';
    }

    return html;
  }

  function generateShowroomModal(data) {
    let html = '';

    if (data.heading) {
      html += `<h2 class="bd-modal__heading subheading--s" id="bd-modal-title">${data.heading}</h2>`;
    }

    if (data.description) {
      html += `<p class="bd-modal__description" style="white-space: pre-wrap;">${data.description}</p>`;
    }

    if (data.locations && data.locations.length > 0) {
      html += '<div class="bd-showroom-list">';

      data.locations.forEach((location, index) => {
        if (!location.city) return;

        html += `
          <details class="bd-showroom-accordion">
            <summary class="bd-showroom-accordion__trigger">
              ${location.icon ? `<img src="${location.icon}" alt="" width="16" height="16.342">` : `
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8L10 3L17 8V17H3V8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              `}
              <span class="text--xs">${location.city}</span>
              ${location.status === 'Coming Soon' ? '<span class="bd-showroom-badge">Coming Soon</span>' : ''}
              <svg class="bd-showroom-accordion__icon" xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9" fill="none">
                <path d="M0.5 0.5L8.5 8.5M8.5 8.5L8.5 2.5M8.5 8.5L2.5 8.5" stroke="#151515" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </summary>
            <div class="bd-showroom-accordion__content">
        `;

        if (location.address) {
          html += `
            <div class="bd-showroom-info">
              <strong class="text--xxs tag--bold">${data.addressLabel || 'Adresse'}:</strong>
              <p class="text--xs">${location.address}</p>
            </div>
          `;
        }

        if (location.hours) {
          html += `
            <div class="bd-showroom-info">
              <strong class="text--xxs tag--bold">${data.hoursLabel || 'Öffnungszeiten'}:</strong>
              <p class="text--xs">${location.hours}</p>
            </div>
          `;
        }

        if (location.variants) {
          html += `
            <div class="bd-showroom-info">
              <strong class="text--xxs tag--bold">${data.modelsLabel || 'Verfügbares Modell'}</strong>
              <div class="bd-showroom-variants">
          `;

          location.variants.forEach(variant => {
            html += `
              <div class="bd-showroom-variant">
                <a href="${variant.url}" class="bd-var-link"></a>
                <div class="bd-showroom-variant__row">
                  ${variant.mainimage ? `<img src="${variant.mainimage}" alt="${variant.name}" >` : ''}
                </div>
                <div class="bd-showroom-variant__row">
                  ${variant.collection ? `<strong>${variant.collection}</strong>` : ''}
                  <span></span>
                </div>
                ${variant.fabric ? `
                  <div class="bd-showroom-variant__row text--xxs">
                    <span>${variant.name}</span>
                    <div class="bd-color">
                    ${variant.color ? `<span class="bd-showroom-color" style="background: url(${variant.color})"></span>` : ''}
                    <span>${variant.fabric}</span>
                    </div>
                  </div>
                ` : ''}
              </div>
            `;
          });

          html += '</div></div>';
        }

        if (location.mapsLink) {
          html += `
            <a href="${location.mapsLink}" target="_blank" rel="noopener noreferrer" class="bd-showroom-map-link text--xs">
              ${data.mapsLinkText || 'In Google Maps öffnen'} →
            </a>
          `;
        }

        html += '</div></details>';
      });

      if (data.allLocationsText) {
        html += `
          <a href="${data.allLocationsLink || '#'}" class="bd-showroom-link text--xs">
            ${data.allLocationsText}
          </a>
        `;
      }

      html += '</div>';
    }

    return html;
  }

  function attachAccordionListeners() {
    const accordions = modalContainer.querySelectorAll('.bd-showroom-accordion');
    accordions.forEach(accordion => {
      const summary = accordion.querySelector('summary');
      summary.addEventListener('click', function(e) {
        // Native browser behavior handles opening/closing
      });
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModals);
  } else {
    initModals();
  }

})();

/* [BD feat] Product Info Modals - Dynamic System */